import { useState, useEffect, useCallback } from "react";
import { CircularProgress, Box } from "@mui/material";

import PaymentDetails from "./components/PaymentDetails";
import RequestPayout from "./components/RequestPayoutModal";
import ConfirmPayout from "./components/ConfirmPayout";
import RequestSubmitted from "./components/RequestSubmitted";
import apiService from "../../../service/Api";
import SelectAccount from "./components/SelectAccount";
import { toast } from "react-toastify";

// ── Types ─────────────────────────────────────────────────────────────────────

interface WalletData {
  balance: string;
  currency: string;
}

interface RequestPayoutsProps {
  open: boolean;
  onClose: () => void;
  referralId: number | string;
}

export interface PayoutAccount {
  id: number;
  method: "ECOCASH" | "INNBUCKS" | "OMARI" | "ONEMONEY" | "BANK";
  masked_ecocash_number?: string;
  bank_name?: string;
  account_name?: string;
  masked_account_number?: string;
  is_default: boolean;
}

const MOBILE_METHODS = ["ECOCASH", "INNBUCKS", "OMARI", "ONEMONEY"];
const METHOD_LABELS: Record<string, string> = {
  ECOCASH: "Ecocash",
  INNBUCKS: "InnBucks",
  OMARI: "Omari",
  ONEMONEY: "OneMoney",
};

// RequestPayouts.tsx
type Step =
  | "loading"
  | "select-account"
  | "payment-details"
  | "request-payout"
  | "confirm"
  | "submitted";

// ── Component ─────────────────────────────────────────────────────────────────

export default function RequestPayouts({
  open,
  onClose,
  referralId,
}: RequestPayoutsProps) {
  const [step, setStep] = useState<Step>("loading");
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [accounts, setAccounts] = useState<PayoutAccount[]>([]);
  const [editingAccount, setEditingAccount] = useState<PayoutAccount | null>(
    null,
  );
  const [selectedAccount, setSelectedAccount] = useState<PayoutAccount | null>(
    null,
  );
  const [amount, setAmount] = useState("");

  // ── Fetch wallet + accounts on open ────────────────────────────────────────

  const loadWalletData = useCallback(async () => {
    setStep("loading");
    try {
      const [walletRes, accountsRes] = await Promise.all([
        apiService.get<WalletData>("/api/wallet/me/"),
        apiService.get<PayoutAccount[]>("/api/wallet/payout-accounts/"),
      ]);
      setWallet(walletRes.data);
      const accts = Array.isArray(accountsRes.data) ? accountsRes.data : [];
      setAccounts(accts);

      // If user already has accounts, skip PaymentDetails

      if (accts.length > 0) {
        setStep("select-account");
      } else {
        setStep("payment-details");
      }
    } catch (err) {
      console.error("Failed to load wallet data", err as Error);
      toast.error("Failed to load your wallet data. Please try again.");
      // Fall through to payment details so user isn't stuck
      setStep("payment-details");
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadWalletData();
    } else {
      // Reset state when closed so next open is fresh
      setStep("loading");
      setAmount("");
      setSelectedAccount(null);
    }
  }, [open, loadWalletData]);

  // ── Submit payout request ──────────────────────────────────────────────────

  const handleConfirmPayout = async () => {
    if (!selectedAccount) return;
    try {
      await apiService.post("/api/wallet/payout-request/", {
        amount,
        payout_account: selectedAccount.id,
      });
      toast.success("Payout request submitted successfully.");
      setStep("submitted");
    } catch (err) {
      toast.error("Failed to submit payout request. Please try again.");
      console.error("Payout request failed", err as Error);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const balance = wallet ? parseFloat(wallet.balance) : 0;

  const accountLabel = (acct: PayoutAccount): string => {
    if (MOBILE_METHODS.includes(acct.method))
      return `${METHOD_LABELS[acct.method]} — ${acct.masked_ecocash_number ?? ""}`;
    return `${acct.bank_name ?? "Bank"} — ${acct.masked_account_number ?? ""}`;
  };

  const accountNumber = selectedAccount
    ? MOBILE_METHODS.includes(selectedAccount.method)
      ? (selectedAccount.masked_ecocash_number ?? "")
      : (selectedAccount.masked_account_number ?? "")
    : "";

  const accountName = selectedAccount
    ? MOBILE_METHODS.includes(selectedAccount.method)
      ? METHOD_LABELS[selectedAccount.method]
      : (selectedAccount.bank_name ?? "Bank")
    : "";

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!open) return null;

  if (step === "loading") {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1300,
          bgcolor: "rgba(0,0,0,0.3)",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <SelectAccount
        open={step === "select-account"}
        onClose={onClose}
        accounts={accounts}
        onSelect={(acct) => {
          setSelectedAccount(acct);
          setStep("request-payout");
        }}
        onAddNew={() => {
          setEditingAccount(null);
          setStep("payment-details");
        }}
      />

      <PaymentDetails
        open={step === "payment-details"}
        onClose={onClose}
        referralId={referralId}
        editingAccount={editingAccount} // null when adding new, set when editing
        onContinue={(savedAccount) => {
          setAccounts((prev) => {
            const exists = prev.some((a) => a.id === savedAccount.id);
            return exists
              ? prev.map((a) => (a.id === savedAccount.id ? savedAccount : a))
              : [...prev, savedAccount];
          });
          setSelectedAccount(savedAccount);
          setStep("request-payout");
        }}
      />

      <RequestPayout
        open={step === "request-payout"}
        onClose={onClose}
        availableBalance={balance}
        accounts={accounts}
        selectedAccount={selectedAccount}
        onSelectAccount={setSelectedAccount}
        amount={amount}
        onAmountChange={setAmount}
        onAddAccount={() => setStep("payment-details")}
        onConfirm={() => setStep("confirm")}
      />

      <ConfirmPayout
        open={step === "confirm"}
        onClose={onClose}
        onBack={() => setStep("request-payout")}
        onEditDetails={() => {
          setEditingAccount(selectedAccount);
          setStep("payment-details");
        }}
        onConfirm={handleConfirmPayout}
        amount={parseFloat(amount) || 0}
        method={accountName}
        phoneNumber={accountNumber}
        accountLabel={selectedAccount ? accountLabel(selectedAccount) : ""}
      />

      <RequestSubmitted
        open={step === "submitted"}
        onClose={onClose}
        onDone={onClose}
        amount={parseFloat(amount) || 0}
        recipientName={accountName}
        lastFourDigits={accountNumber.slice(-4)}
      />
    </>
  );
}
